import type { userAgent } from 'next/server';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useUser from '@alltrails/context/hooks/useUser';
import useLoadRecurly from '@alltrails/core/hooks/useLoadRecurly';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import SubscriptionsNewResponse from '@alltrails/core/types/SubscriptionsNewResponse';
import useLoadApplePaySDK from '@alltrails/payment/hooks/useLoadApplePaySDK';
import CheckoutModal from './CheckoutModal';
import styles from './CheckoutModal.module.scss';
import getSubscriptionsNew from './getSubscriptionsNew';

type Props = {
  navigateToWelcome: () => void;
  onSuccess?: () => void;
  purchaseTier: PurchaseTier;
  trigger: UpsellTrigger;
};

export default function CheckoutModalStepContent({ navigateToWelcome, onSuccess, purchaseTier, trigger }: Props) {
  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isRecurlyLoaded = useLoadRecurly();
  const isApplePaySDKLoaded = useLoadApplePaySDK();

  const [isLoaded, setIsLoaded] = useState(false);
  const [subscriptionsNew, setSubscriptionsNew] = useState<SubscriptionsNewResponse>();
  const user = useUser();

  const isRadioUiEnabled = !browserName?.includes('Chrome');

  useEffect(() => {
    async function getPlans() {
      const response = await getSubscriptionsNew();
      setSubscriptionsNew(response);
    }

    getPlans();
  }, [purchaseTier]);

  // For non-radio button UI, loading is done when recurly, availablePlans, and Apple Pay SDK are loaded.
  // For radio button UI, loading is done when recurly, availablePlans, GooglePay, and Apple Pay SDK are loaded (or until a 3 second timer completes).
  useEffect(() => {
    if (!isRadioUiEnabled && isRecurlyLoaded && subscriptionsNew && isApplePaySDKLoaded) {
      setIsLoaded(true);
    }

    if (isRadioUiEnabled && isRecurlyLoaded && subscriptionsNew && isApplePaySDKLoaded) {
      recurly.configure(process.env.NEXT_PUBLIC_RECURLY_KEY);

      const googlePay = recurly.GooglePay({
        buttonOptions: {
          buttonType: 'plain',
          buttonSizeMode: 'fill'
        },
        currency: subscriptionsNew.currencyCode,
        country: 'US', // this is the merchants country which is always US
        googleMerchantId: 'BCR2DN4TQTTI56B4'
      });

      googlePay.on('ready', () => {
        clearTimeout(timeout);
        setIsLoaded(true);
      });

      googlePay.on('error', () => {
        clearTimeout(timeout);
        setIsLoaded(true);
      });

      // Set a timeout to wait for 3 seconds in case the GooglePay events don't fire.
      const timeout = setTimeout(() => {
        setIsLoaded(true);
      }, 3000);

      return () => clearTimeout(timeout);
    }

    return;
  }, [isRecurlyLoaded, setIsLoaded, isLoaded, subscriptionsNew, isRadioUiEnabled, isApplePaySDKLoaded]);

  if (!isLoaded || !subscriptionsNew || !user) {
    return null;
  }

  const plans = purchaseTier === 'peak' ? subscriptionsNew.plans.peak : subscriptionsNew.plans.plus;

  return (
    <div className={styles.checkoutModalContainer}>
      <CheckoutModal
        initialNonTrialPlan={plans.nonTrial}
        initialPlan={user.isPromoEligible ? plans.trial : plans.nonTrial}
        navigateToWelcome={navigateToWelcome}
        onSuccess={onSuccess}
        purchaseTier={purchaseTier}
        subscriptionsNewResponse={subscriptionsNew}
        subscriptionTier={user.subscriptionTier}
        trigger={trigger}
        userId={user.id}
      />
    </div>
  );
}
